import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axiosClient from 'http/axios-client';
import * as XLSX from 'xlsx';
import LoadingButton, { LoadingButtonNoGrid } from 'components/basic/LoadingButton';
import { useSerialsPerThematic } from 'hooks/serial-numbers-hooks';
import { Button, Dialog, DialogContent, DialogActions, Typography } from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import CircularProgress from '@material-ui/core/CircularProgress';
import * as _ from 'lodash';
import { parseDate } from '../consents/ConsentsTable';
import AppTable from '../../basic/AppTable';
import { filterTypes } from '../../basic/AdvancedFilters';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import DisableAppointmentSwitch from './DisableAppointmentSwitch';

async function isIndividual(kits) {
  const results = await Promise.all(
    kits.map(async (kit) => {
      const kitsData = await axiosClient.get(`/registration-kits/?serial_number_id.serial=${ kit.serial }`);
      if (kitsData[0]?.business === null && kitsData[0]?.doctor === null) {
        return kit.serial.toString();
      } return null;
    }),
  );
  return results;
}

const SerialNumbersTable = (props) => {
  const [ filterQuery, setFilterQuery ] = useState({});
  const { locationFilter } = props;
  /*
  const {
    items: serialNumbers,
    itemsLoading: serialNumbersLoading,
    pagination,
  } = useItems(CollectionKeys.SERIAL_NUMBER, { filterQuery }); */

  const newEndpoint = `${ CollectionKeys.SERIAL_NUMBER }/thematic-package`;

  const [ deleteConfirmOpen, setDeleteConfirmOpen ] = useState(false);
  const [ serialToBeDeleted, setSerialToBeDeleted ] = useState(null);
  const [ deleteLoading, setDeleteLoading ] = useState(false);
  const [ isDownloading, setIsDownloading ] = useState(false);

  // table data
  const {
    serials: serialNumbersPerThematic,
    serialsLoading: serialNumbersPerThematicLoading,
    pagination: serialsPerThematicPages,
    refetch: serialNumbersRefetch,
    updateSerial,
  } = useSerialsPerThematic(newEndpoint, props.packageId, { filterQuery: { ...filterQuery, ...locationFilter } });

  // getting data if the serial is registered by individual, in order to show or not the appointment switch
  const {
    data: individuals,
    isLoading: loadingIndividuals,
    isFetching: fetchingIndividuals,
    error: errorIndividuals,
  } = useQuery(
    [ 'individuals', serialNumbersPerThematic ],
    () => { return isIndividual(serialNumbersPerThematic); },
    {
      enabled: !!serialNumbersPerThematic && serialNumbersPerThematic.length > 0,
    },
  );

  const tableColumns = [
    { id: 1, label: 'ID', field: 'id' },
    { id: 2, label: 'Value', field: 'serial' },
    { id: 3, label: 'Status', field: 'status' },
    { id: 3333,
      label: 'Report payment status',
      field: 'report_payment_status',
      serial_payment_dropdown: true,
      updateSerial },
    { id: 3334,
      label: 'Kit payment status',
      field: 'kit_payment_status',
      serial_payment_dropdown: true,
      updateSerial },
    { id: 4,
      label: 'Updated At',
      field: 'updated_at',

      customRenderer({ row }) {
        return (
          <>
            {parseDate(row.updated_at)}
          </>
        );
      } },
    { id: 5,
      label: 'Created At',
      field: 'created_at',
      customRenderer({ row }) {
        return (
          <>
            {parseDate(row.created_at)}
          </>
        );
      } },
    { id: 6,
      label: 'Sample Available',
      field: 'sample_available',
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
            {row.sample_available && (
              <DoneIcon style={ { color: '#93C120' } } />
            )}
            {!row.sample_available && (
              <ClearIcon style={ { color: 'red' } } />
            )}
          </div>
        );
      } },
    { id: 7,
      label: 'Health Data Available',
      field: 'health_data_available',
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
            {row.health_data_available && (
              <DoneIcon style={ { color: '#93C120' } } />
            )}
            {!row.health_data_available && (
              <ClearIcon style={ { color: 'red' } } />
            )}
          </div>
        );
      } },

    { id: 77,
      label: 'Upgradable',
      field: 'is_upgradeable',
      customRenderer({ row }) {
        return (
          <div style={ { display: 'flex', justifyContent: 'flex-start' } }>
            {true === row.is_upgradeable && (
              <DoneIcon style={ { color: '#93C120' } } />
            )}
            {true !== row.is_upgradeable && (
              <ClearIcon style={ { color: 'red' } } />
            )}
          </div>
        );
      } },

    { id: 78,
      label: 'Appointment Availability',
      field: 'appointment_availability',
      customRenderer({ row }) {
        if (loadingIndividuals || fetchingIndividuals) {
          return <CircularProgress size={ 20 } />;
        }
        if (!loadingIndividuals && !errorIndividuals && individuals.includes(row.serial)) {
          return (
            <DisableAppointmentSwitch
              appointmentAvailable={ row.appointment_availability }
              id={ row.id }
            />
          );
        }
        return <></>;
      } },
    { id: 8,
      label: 'Action',
      noSort: true,
      field: '',
      customRenderer({ row }) {
        return (
          <Button
            variant='contained'
            disabled={ 'valid' === row.status }
            style={ { background: 'valid' === row.status ? '' : 'red', color: 'valid' === row.status ? '' : 'white' } }
            onClick={ () => {
              setSerialToBeDeleted(row.serial);
              setDeleteConfirmOpen(true);
            } }
          >
            Delete kit
          </Button>
        );
      } },
  ];

  const deleteKit = () => {
    if (null != serialToBeDeleted) {
      setDeleteLoading(true);
      axiosClient.delete(`registration-kits/${ serialToBeDeleted }`).finally(() => {
        serialNumbersRefetch().finally(() => {
          setDeleteLoading(false);
          setSerialToBeDeleted(null);
          setDeleteConfirmOpen(false);
        });
      });
    } else {
      console.error('No kit selected');
    }
  };

  const downloadSerialInfo = async () => {
    setIsDownloading(true);
    const theSerials = await axiosClient.get(CollectionKeys.SERIAL_NUMBER, {
      params: {
        _limit: -1,
        'thematic_package.id_eq': props.packageId,
      },
    });

    if (null == theSerials || 0 === theSerials.length) {
      setIsDownloading(false);
      return;
    }

    const data = theSerials.map((serial) => {
      return {
        'Serial Number': serial.serial ?? '',
        Status: serial.status ?? '',
        'Thematic Package': serial?.thematic_package?.real_name ?? '',
        'Sample Avaialble': serial?.sample_available ?? '',
        'Health data available': serial?.health_data_available ?? '',
        'Created at': serial?.created_at ?? '',
        'Report paid - Kit paid': `${ serial?.report_payment_status ?? 'none' } - ${ serial?.kit_payment_status ?? 'none' }`,
      };
    });

    function fitToColumn(arrayOfArray) {
      return Object.keys(arrayOfArray[0]).map((key) => {
        return { wch: key.length };
      });
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = fitToColumn(data);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, 'Serial Numbers.xlsx');

    setIsDownloading(false);
  };

  return (
    <>
      <LoadingButton
        loading={ isDownloading }
        label='Download all'
        style={ { maxWidth: 'unset' } }
        color='secondary'
        variant='contained'
        onClick={ downloadSerialInfo }
      />

      <Dialog
        open={ deleteConfirmOpen }
        onClose={ () => { setDeleteConfirmOpen(false); } }
        fullWidth
        maxWidth='sm'
      >
        <DialogContent>
          <Typography>
            Are you sure you want to delete the kit?
          </Typography>

          <DialogActions>
            <LoadingButtonNoGrid
              loading={ deleteLoading }
              onClick={ () => { deleteKit(); } }
              label='Yes'
              variant='contained'
              color='secondary'
              buttonStyle={ { color: 'white' } }
            />

            <Button
              variant='contained'
              style={ { background: 'red', color: 'white' } }
              onClick={ () => { setDeleteConfirmOpen(false); } }
            >
              No
            </Button>
          </DialogActions>
        </DialogContent>

      </Dialog>

      <AppTable
        columns={ tableColumns }
        data={ serialNumbersPerThematic }
        loading={ serialNumbersPerThematicLoading }
        paginationProps={ serialsPerThematicPages }
        advancedFiltersProps={ {
          properties: [
            {
              value: 'status',
              label: 'Status',
              type: filterTypes.CATEGORY,
              options: [
                { value: 'correlated', label: 'correlated' },
                { value: 'valid', label: 'valid' },
              ],
            },
            {
              value: 'id',
              label: 'ID',
              type: filterTypes.NUMERIC,
            },
            {
              value: 'serial',
              label: 'Serial',
              type: filterTypes.FREE_TEXT,
            },
          ],
          initialAndFilterOptions: [],
          onQueryChange: setFilterQuery,
        } }
      />
    </>
  );
};

export default SerialNumbersTable;

